import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../LanguageContext';
import SEO from '../components/SEO';
import { api } from '../App';
import './FirstCryptoFund.css';

const FirstCryptoFund = () => {
  const [navData, setNavData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchNavData = async () => {
      try {
        const data = await api.get('/api/fund-nav/fcf');
        setNavData(data.navData || []);
      } catch (error) {
        console.error('Failed to fetch NAV data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNavData();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'sl' ? 'sl-SI' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-date">{formatDate(label)}</p>
          <p className="tooltip-value">
            <span className="tooltip-dot"></span>
            {t('fcf.navUsd')}: <strong>{payload[0].value.toFixed(2)}</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fcf-page">
      <SEO 
        title={t('fcf.seoTitle')}
        description={t('fcf.seoDescription')}
        keywords="first crypto fund, cryptocurrency investment, masternodes, staking, yield farming, crypto fund Slovenia"
      />

      {/* Hero Section */}
      <section className="fcf-hero">
        <div className="container">
          <motion.div
            className="fcf-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="fund-notice">{t('fcf.fundNotice')}</p>
          </motion.div>
        </div>
      </section>

      {/* Chart Section */}
      <section className="fcf-chart-section section">
        <div className="container">
          <motion.div
            className="chart-container"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="chart-title">{t('fcf.chartTitle')}</h1>
            <p className="chart-subtitle">{t('fcf.chartSubtitle')}</p>
            
            {loading ? (
              <div className="chart-loading">{t('fcf.loadingChart')}</div>
            ) : navData.length > 0 ? (
              <div className="chart-wrapper">
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={navData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                    <defs>
                      <linearGradient id="navGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4169E1" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#4169E1" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDate}
                      stroke="var(--text-muted)"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      stroke="var(--text-muted)"
                      tick={{ fontSize: 12 }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="nav" 
                      stroke="#4169E1" 
                      strokeWidth={2}
                      fill="url(#navGradient)" 
                      dot={{ fill: '#4169E1', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#4169E1' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="chart-legend">
                  <span className="legend-item">
                    <span className="legend-dot"></span>
                    {t('fcf.navUsd')}
                  </span>
                </div>
              </div>
            ) : (
              <div className="chart-empty">{t('fcf.noChartData')}</div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Strategy Section */}
      <section className="fcf-strategy section">
        <div className="container">
          <motion.div
            className="strategy-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">{t('fcf.strategyTitle')}</h2>
            <p>{t('fcf.strategyP1')}</p>
            <p>{t('fcf.strategyP2')}</p>
            <p>{t('fcf.strategyP3')}</p>
            <p>{t('fcf.strategyP4')}</p>

            <div className="strategy-methods">
              <div className="method-card">
                <h3>{t('fcf.masternodesTitle')}</h3>
                <p>{t('fcf.masternodesDesc')}</p>
              </div>

              <div className="method-card">
                <h3>{t('fcf.stakingTitle')}</h3>
                <p>{t('fcf.stakingDesc')}</p>
              </div>

              <div className="method-card">
                <h3>{t('fcf.yieldFarmingTitle')}</h3>
                <p>{t('fcf.yieldFarmingDesc')}</p>
              </div>

              <div className="method-card">
                <h3>{t('fcf.vestingTitle')}</h3>
                <p>{t('fcf.vestingDesc')}</p>
              </div>

              <div className="method-card">
                <h3>{t('fcf.lpTitle')}</h3>
                <p>{t('fcf.lpDesc')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="fcf-advantages section">
        <div className="container">
          <motion.div
            className="advantages-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">{t('fcf.advantagesTitle')}</h2>
            <p className="advantages-intro">{t('fcf.advantagesIntro')}</p>
            
            <ul className="advantages-list">
              <li>
                <strong>{t('fcf.advantage1Title')}</strong> {t('fcf.advantage1Desc')}
              </li>
              <li>
                <strong>{t('fcf.advantage2Title')}</strong> {t('fcf.advantage2Desc')}
              </li>
              <li>
                <strong>{t('fcf.advantage3Title')}</strong> {t('fcf.advantage3Desc')}
              </li>
              <li>
                <strong>{t('fcf.advantage4Title')}</strong> {t('fcf.advantage4Desc')}
              </li>
            </ul>
            
            <p className="advantages-conclusion">{t('fcf.advantagesConclusion')}</p>
          </motion.div>
        </div>
      </section>

      {/* Technical Information Section */}
      <section className="fcf-technical section">
        <div className="container">
          <motion.div
            className="technical-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">{t('fcf.technicalTitle')}</h2>
            
            <div className="technical-grid">
              <div className="tech-item">
                <span className="tech-label">{t('fcf.isinCode')}:</span>
                <span className="tech-value">SI0037801766</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.securityCode')}:</span>
                <span className="tech-value">APXCF</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.cfiGroup')}:</span>
                <span className="tech-value">{t('fcf.cfiValue')}</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.leiCode')}:</span>
                <span className="tech-value">485100YG4L723GO3I350</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.registrationNumber')}:</span>
                <span className="tech-value">8557012003</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.taxNumber')}:</span>
                <span className="tech-value">56003609</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.fundType')}:</span>
                <span className="tech-value">{t('fcf.fundTypeValue')}</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.registeredIn')}:</span>
                <span className="tech-value">{t('fcf.registeredInValue')}</span>
              </div>
              <div className="tech-item full-width">
                <span className="tech-label">{t('fcf.supervisoryAuthority')}:</span>
                <span className="tech-value">
                  {t('fcf.supervisoryValue')} <a href="https://www.a-tvp.si/eng" target="_blank" rel="noopener noreferrer">https://www.a-tvp.si/eng</a>)
                </span>
              </div>
              <div className="tech-item full-width">
                <span className="tech-label">{t('fcf.reporting')}:</span>
                <span className="tech-value">{t('fcf.reportingValue')}</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.risk')}:</span>
                <span className="tech-value risk-high">{t('fcf.riskValue')}</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.investmentStrategy')}:</span>
                <span className="tech-value">{t('fcf.investmentStrategyValue')}</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.currency')}:</span>
                <span className="tech-value">{t('fcf.currencyValue')}</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.minInvestment')}:</span>
                <span className="tech-value">20.000,00</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.entryFee')}:</span>
                <span className="tech-value">5%</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.exitFee')}:</span>
                <span className="tech-value">0%</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.transactionCost')}:</span>
                <span className="tech-value">0%</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.adminFee')}:</span>
                <span className="tech-value">0%</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.managementFee')}:</span>
                <span className="tech-value">3% p.a.</span>
              </div>
              <div className="tech-item">
                <span className="tech-label">{t('fcf.otherFees')}:</span>
                <span className="tech-value">0%</span>
              </div>
              <div className="tech-item full-width">
                <span className="tech-label">{t('fcf.profitSharing')}:</span>
                <span className="tech-value">{t('fcf.profitSharingValue')}</span>
              </div>
              <div className="tech-item full-width">
                <span className="tech-label">{t('fcf.cutOffDate')}:</span>
                <span className="tech-value">{t('fcf.cutOffDateValue')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Payment Instructions Section */}
      <section className="fcf-payment section">
        <div className="container">
          <motion.div
            className="payment-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">{t('fcf.paymentTitle')}</h2>
            
            <div className="payment-card">
              <div className="payment-item">
                <span className="payment-label">{t('fcf.iban')}:</span>
                <span className="payment-value">SI56 0201 1026 3594 410</span>
              </div>
              <div className="payment-item">
                <span className="payment-label">{t('fcf.bicSwift')}:</span>
                <span className="payment-value">LJBASI2X</span>
              </div>
              <div className="payment-item">
                <span className="payment-label">{t('fcf.bank')}:</span>
                <span className="payment-value">NLB d.d., Ljubljana, Slovenia</span>
              </div>
              <div className="payment-item">
                <span className="payment-label">{t('fcf.manager')}:</span>
                <span className="payment-value">FUTURE MONEY d.o.o., Ljubljana, Slovenia, EU</span>
              </div>
              <div className="payment-item">
                <span className="payment-label">{t('fcf.address')}:</span>
                <span className="payment-value">Dunajska cesta 101, 1000 Ljubljana, Slovenia</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FirstCryptoFund;
